import type { Actions, Subjects } from '@/libs/ability'

export const APP_ROUTES = {
  home: '/',
  auth: {
    login: '/login',
    adminLogin: '/admin-login',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
    adminForgotPassword: '/admin/forgot-password',
    adminResetPassword: '/admin/reset-password',
  },
  acceptInvite: '/accept-invite',
  account: '/account',
  changePassword: '/change-password',
  selectOrganization: '/select-organization',
  accessDenied: '/access-denied',
  somethingWentWrong: '/error',
  maintenance: '/maintenance',
  leaves: '/leaves',
  workLogs: '/work-logs',
  myWorkspace: '/my-workspace',
  teamOverview: '/team-overview',
  billing: '/billing',
  invoicing: {
    base: '/invoicing',
    new: '/invoicing/new',
    details: (id: string) => `/invoicing/${id}`,
  },
  economy: '/economy',
  pricing: '/pricing',
  roles: '/roles',
  campaigns: '/campaigns',
  integrations: '/integrations',
  loanCars: '/loan-cars',
  category: {
    base: '/categories',
    type: (categoryType: string) => `/categories/${categoryType}`,
    create: (categoryType: string) => `/categories/${categoryType}/new`,
    edit: (categoryType: string, id: string) => `/categories/${categoryType}/${id}/edit`,
    details: (categoryType: string, id: string) => `/categories/${categoryType}/${id}`,
  },
  customer: {
    base: '/customers',
    type: (customerEntityType: string) => `/customers/${customerEntityType}`,
    create: (customerEntityType: string) => `/customers/${customerEntityType}/new`,
    details: (id: string) => `/customers/${id}`,
    new: `/customers/new`,
    company: {
      base: '/customers/companies',
      create: `/customers/companies/new`,
      details: (id: string) => `/customers/companies/${id}`,
    },
  },
  communication: {
    base: '/communication',
    type: (templateType: string) => `/communication/${templateType}`,
    create: (templateType: string) => `/communication/${templateType}/new`,
    details: (templateType: string, id: string) => `/communication/${templateType}/${id}`,
  },
  dashboard: '/dashboard',
  booking: {
    base: '/booking',
    details: (id: string) => `/booking/${id}`,
    new: (customerID?: string, vehicleID?: string) =>
      `/booking/new${customerID ? `?customerID=${customerID}` : ''}${customerID ? '&' : vehicleID ? '?' : ''}${vehicleID ? `vehicleID=${vehicleID}` : ''}`,
  },
  employee: {
    base: '/employees',
    details: (id: string) => `/employees/${id}`,
    edit: (id: string) => `/employees/${id}/edit`,
    new: `/employees/new`,
  },
  rentCar: {
    base: '/rental-fleet',
    new: `/rental-fleet/new`,
    details: (id: string) => `/rental-fleet/${id}`,
  },

  settings: {
    base: '/settings',
    branchInfo: '/settings/branch-info',
    workingHours: '/settings/working-hours',
    permissions: '/settings/permissions',
    weeklyNotes: '/settings/weekly-notes',
    stores: {
      base: '/settings/stores',
      details: (id: string) => `/settings/stores/${id}`,
      new: `/settings/stores/new`,
    },
    qualifications: {
      base: '/settings/qualifications',
      details: (type: string, id: string) => `/settings/qualifications/${id}?type=${type}`,
      new: (type: string) => `/settings/qualifications/new?type=${type}`,
    },
    staff: {
      base: '/settings/staff',
      details: (id: string) => `/settings/staff/${id}`,
    },
    unionAgreements: {
      base: '/settings/union-agreements',
      details: (id: string) => `/settings/union-agreements/${id}`,
      new: '/settings/union-agreements/new',
    },
    category: {
      base: '/settings/categories',
      type: (categoryType: string) => `/settings/categories/${categoryType}`,
      create: (categoryType: string) => `/settings/categories/${categoryType}/new`,
      edit: (categoryType: string, id: string) => `/settings/categories/${categoryType}/${id}/edit`,
      details: (categoryType: string, id: string) => `/settings/categories/${categoryType}/${id}`,
    },
    packages: {
      base: '/settings/packages',
      new: '/settings/packages/new',
      details: (id: string) => `/settings/packages/${id}`,
    },
    absenceTypes: {
      base: '/settings/absence-types',
      new: '/settings/absence-types/new',
      details: (id: string) => `/settings/absence-types/${id}`,
    },
    service: {
      base: '/settings/services',
      new: `/settings/services/new`,
      edit: (id: string) => `/settings/services/${id}/edit`,
      details: (id: string) => `/settings/services/${id}`,
    },
    communication: {
      base: '/settings/communication',
      type: (templateType: string) => `/settings/communication/${templateType}`,
      create: (templateType: string) => `/settings/communication/${templateType}/new`,
      details: (templateType: string, id: string) =>
        `/settings/communication/${templateType}/${id}`,
    },
  },
  vehicle: {
    base: '/vehicles',
    details: (id: string) => `/vehicles/${id}`,
    new: `/vehicles/new`,
  },
  job: {
    base: '/jobs',
    details: (id: string) => `/jobs/${id}`,
    new: `/jobs/new`,
  },
  product: {
    base: '/products',
    details: (id: string) => `/products/${id}`,
    new: `/products/new`,
  },
  admin: {
    base: '/admin',
    organizations: '/admin/organizations',
    newOrganization: '/admin/organizations/new',
    organizationDetails: (id: string) => `/admin/organizations/${id}`,
    subscriptions: '/admin/subscriptions',
    newSubscription: '/admin/subscriptions/new',
    subscriptionDetails: (id: string) => `/admin/subscriptions/${id}`,
    plans: '/admin/plans',
    newPlan: '/admin/plans/new',
    planDetails: (id: string) => `/admin/plans/${id}`,
    discounts: '/admin/discounts',
    newDiscount: '/admin/discounts/new',
    discountDetails: (id: string) => `/admin/discounts/${id}`,
    globalRoles: '/admin/roles',
    newGlobalRole: '/admin/roles/new',
    globalRoleDetails: (id: string) => `/admin/roles/${id}`,
    globalPermissions: '/admin/permissions',
    newGlobalPermission: '/admin/permissions/new',
    globalPermissionDetails: (id: string) => `/admin/permissions/${id}`,
    users: '/admin/users',
    userDetails: (id: string) => `/admin/users/${id}`,
    features: '/admin/features',
    newFeature: '/admin/features/new',
    featureDetails: (id: string) => `/admin/features/${id}`,
    usage: '/admin/usage',
    usageDetails: (id: string) => `/admin/usage/${id}`,
    salesCompanies: '/admin/sales-companies',
    newSalesCompany: '/admin/sales-companies/new',
    salesCompanyDetails: (id: string) => `/admin/sales-companies/${id}`,
    account: '/admin/account',
  },
}

type RoutePermission = {
  requiredAction: Actions
  requiredSubject: Subjects
}

const perm = (requiredAction: Actions, requiredSubject: Subjects): RoutePermission => ({
  requiredAction,
  requiredSubject,
})

export const ROUTE_PERMISSIONS = {
  customers: {
    read: perm('read', 'Customer'),
    create: perm('create', 'Customer'),
  },
  company: {
    read: perm('read', 'Company'),
    create: perm('create', 'Company'),
  },
  vehicles: {
    read: perm('read', 'Vehicle'),
    create: perm('create', 'Vehicle'),
  },
  services: {
    read: perm('read', 'Service'),
    create: perm('create', 'Service'),
  },
  jobs: {
    read: perm('read', 'Job'),
    create: perm('create', 'Job'),
  },
  booking: {
    read: perm('read', 'Job'),
    create: perm('create', 'Job'),
  },
  leaves: {
    read: perm('read', 'Leave'),
  },
  workLogs: {
    read: perm('read', 'WorkLog'),
  },
  billing: {
    read: perm('read', 'Billing'),
    create: perm('create', 'Billing'),
    update: perm('update', 'Billing'),
  },
  myWorkspace: {
    read: perm('read', 'WorkLog'),
  },
  teamOverview: {
    read: perm('read', 'WorkLog'),
  },
  products: {
    read: perm('read', 'Product'),
    create: perm('create', 'Product'),
  },
  rentalFleet: {
    read: perm('read', 'LoanCar'),
    create: perm('create', 'LoanCar'),
  },
  campaigns: {
    read: perm('read', 'Campaign'),
  },
  pricing: {
    read: perm('read', 'Pricing'),
  },
  categories: {
    read: perm('read', 'Category'),
    create: perm('create', 'Category'),
  },
  communication: {
    read: perm('read', 'Communication'),
    create: perm('create', 'Communication'),
  },
  settings: {
    read: perm('read', 'Settings'),
  },
  staff: {
    read: perm('read', 'User'),
    create: perm('create', 'User'),
    update: perm('update', 'User'),
    delete: perm('delete', 'User'),
  },
  stores: {
    read: perm('read', 'Store'),
    create: perm('create', 'Store'),
    update: perm('update', 'Store'),
    delete: perm('delete', 'Store'),
  },
  qualifications: {
    read: perm('read', 'Qualification'),
    create: perm('create', 'Qualification'),
    update: perm('update', 'Qualification'),
    delete: perm('delete', 'Qualification'),
  },
  unionAgreements: {
    read: perm('read', 'UnionAgreement'),
    create: perm('create', 'UnionAgreement'),
    update: perm('update', 'UnionAgreement'),
    delete: perm('delete', 'UnionAgreement'),
  },
  packages: {
    read: perm('read', 'PackageDeal'),
    create: perm('create', 'PackageDeal'),
    update: perm('update', 'PackageDeal'),
    delete: perm('delete', 'PackageDeal'),
  },
  absenceTypes: {
    read: perm('read', 'Leave'),
    create: perm('create', 'Leave'),
    update: perm('update', 'Leave'),
    delete: perm('delete', 'Leave'),
  },
}
